import type { ReactNode } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { InterviewRow } from '../services/interviewsApi';
import { InterviewRescheduleButton } from './InterviewRescheduleButton';
import { GmailConnectionProvider } from './taActionBoard/GmailConnectionContext';
import { PendingActionsBanner } from './taActionBoard/BoardChrome';
import { RescheduleProvider, useReschedule } from './taActionBoard/RescheduleContext';
import type { PipelineRow } from './taActionBoard/pipelineRows';
import { useGmailToken } from './taActionBoard/useGmailToken';
import { usePendingActions } from './taActionBoard/usePendingActions';

/**
 * The Log's "Move…" for an opening interview offers the board's choice: send the candidate a link to
 * pick a new time themselves, or pick one for them. The Log needs what the board gives that dialog —
 * the recruiter's Gmail, the ten-second countdown behind a send — so this provider mounts them.
 */
export function LogRescheduleProvider({
  me,
  onChanged,
  children,
}: {
  me: string | null;
  onChanged: () => void;
  children: ReactNode;
}) {
  // No signed-in address means no mailbox to send from: the rows then keep the plain Move dialog.
  if (!me) return <>{children}</>;
  return (
    <SignedInProvider me={me} onChanged={onChanged}>
      {children}
    </SignedInProvider>
  );
}

function SignedInProvider({
  me,
  onChanged,
  children,
}: {
  me: string;
  onChanged: () => void;
  children: ReactNode;
}) {
  const gmail = useGmailToken(me);
  const { pending, defer, cancel } = usePendingActions();
  return (
    <GmailConnectionProvider value={gmail}>
      <PendingActionsBanner pending={pending} onCancel={cancel} />
      <RescheduleProvider boardEmail={me} onDefer={defer} onChanged={onChanged} surface="log">
        {children}
      </RescheduleProvider>
    </GmailConnectionProvider>
  );
}

/** The fields the dialog reads from a board row; the Log row carries all of them. */
function toPipelineRow(row: InterviewRow): PipelineRow {
  return {
    opportunityId: row.opportunityId ?? '',
    name: row.candidateName || row.candidateEmail || 'this candidate',
    leverUrl: '',
    round: 'first',
    stageName: row.stageName ?? '',
    state: 'booked',
    start: row.start,
    past: false,
    inLever: Boolean(row.leverPanelId),
    invitedAt: null,
    invitedBy: null,
    interviewers: row.interviewers,
    mode: null,
    retryable: false,
    bookingRef: row.bookingRef,
    noShow: null,
    noShowAt: null,
    earlier: [],
  };
}

/**
 * A booking with an interview link (`meetingType` is set only then) can be sent a link; case and
 * final-round bookings have none, and keep the plain Move dialog.
 */
export function LogMoveButton({
  row,
  onChanged,
}: {
  row: InterviewRow;
  onChanged: () => void;
}) {
  const reschedule = useReschedule();
  if (!reschedule || !row.meetingType) {
    return (
      <InterviewRescheduleButton
        bookingRef={row.bookingRef}
        candidateName={row.candidateName}
        currentStart={row.start}
        onRescheduled={onChanged}
        surface="log"
      />
    );
  }
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={() => reschedule.openReschedule(toPipelineRow(row), row.postingId ?? '')}
    >
      Move…
    </Button>
  );
}
